/**
 * Utility-Funktionen für Parcel-Status und Farben
 */
import { colors } from "../../styles/colors";
import type { StatusVisual } from "./statusVisual";

export interface StatusOption {
  value: string;
  label: string;
  color: string;
  fillOpacity: number;
}

// Status-Optionen mit Farben und Transparenz (Values müssen mit Backend ParcelStatus Enum übereinstimmen)
// Farben aus dem zentralen Farbsystem (colors.parcelStatus)
export const STATUS_OPTIONS: readonly StatusOption[] = [
  {
    value: "Kein Kontakt",
    label: "Kein Kontakt",
    color: colors.parcelStatus.keinKontakt,
    fillOpacity: 0.35,
  },
  {
    value: "Kontaktiert",
    label: "Kontaktiert",
    color: colors.parcelStatus.kontaktiert,
    fillOpacity: 0.6,
  },
  {
    value: "Abgelehnt",
    label: "Abgelehnt",
    color: colors.parcelStatus.abgelehnt,
    fillOpacity: 0.6,
  },
  {
    value: "Vertrag abgeschlossen",
    label: "Abgeschlossen",
    color: colors.parcelStatus.abgeschlossen,
    fillOpacity: 0.4,
  },
] as const;

/**
 * Ermittelt das Display-Label für einen gegebenen Status-Wert
 * Konvertiert den technischen Wert (z.B. "Vertrag abgeschlossen") in das Anzeige-Label (z.B. "Abgeschlossen")
 */
export const getStatusLabel = (status?: string): string => {
  const statusOption = STATUS_OPTIONS.find(
    (option) => option.value === (status || "Kein Kontakt"),
  );
  return statusOption?.label || status || "Kein Kontakt";
};

/**
 * Ermittelt die Farbe für einen gegebenen Parcel-Status
 */
export const getStatusColor = (status?: string): string => {
  const statusOption = STATUS_OPTIONS.find(
    (option) => option.value === (status || "Kein Kontakt"),
  );
  return statusOption?.color || STATUS_OPTIONS[0].color; // Fallback auf ersten Status
};

/**
 * Status eines Flurstücks oder Eigentümers für `StatusChip` – wie die Chips in der
 * Flächensicherung: Hintergrund in der Statusfarbe, Schrift schwarz
 */
export const getParcelStatusVisual = (status?: string): StatusVisual => ({
  label: getStatusLabel(status),
  color: colors.utility.black,
  background: getStatusColor(status),
});

/**
 * Ermittelt die Status-Option für einen gegebenen Status
 */
export const getStatusOption = (status?: string): StatusOption => {
  return (
    STATUS_OPTIONS.find(
      (option) => option.value === (status || "Kein Kontakt"),
    ) || STATUS_OPTIONS[0]
  );
};

/**
 * Standard-Polygon-Style für Leaflet mit statusabhängiger Transparenz
 */
export const getPolygonStyle = (status?: string) => {
  const statusOption = getStatusOption(status);
  return {
    color: statusOption.color,
    weight: 2,
    opacity: 1.0,
    fillColor: statusOption.color,
    fillOpacity: statusOption.fillOpacity,
  };
};

/**
 * Hover-Style für Flurstück-Polygone (verstärkte Sichtbarkeit beim Überfahren)
 */
export const getPolygonHoverStyle = (status?: string) => {
  const statusOption = getStatusOption(status);
  return {
    color: statusOption.color,
    weight: 3, // Dickere Umrandung beim Hover
    opacity: 1.0, // Vollständig sichtbare Umrandung
    fillColor: statusOption.color,
    fillOpacity: Math.min(statusOption.fillOpacity + 0.3, 1.0), // Erhöhte Füll-Transparenz
  };
};

/**
 * Selected-Style für ausgewählte Flurstück-Polygone (deutlich sichtbar)
 */
export const getPolygonSelectedStyle = (status?: string) => {
  const statusOption = getStatusOption(status);
  return {
    color: statusOption.color,
    weight: 4, // Dickerer Rand für ausgewähltes Flurstück
    opacity: 1.0, // Vollständig sichtbare Umrandung
    fillColor: statusOption.color,
    fillOpacity: Math.min(statusOption.fillOpacity + 0.4, 0.9), // Dunklere Füllung
  };
};
